import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { StackActions } from '@react-navigation/native';

import IcomoonIcon from '../../components/IcomoonIcon';
import { Icon } from '../../components/Icon';
import { Illustration } from '../../components/Illustration';
import Text from '../../components/Text';
import tw from '../../styles/tailwind';
import { SidebarItem, visibleSidebarItems } from './sidebarStructure';
import useActiveAdminCommunity from '../../hooks/useActiveAdminCommunity';


type Props = {
  state: any;
  descriptors: any;
  navigation: any;
};


export default function AdminSidebar({ state, navigation }: Props) {
  console.log('%c AdminSidebar render', 'color: cyan', state);

  const { community } = useActiveAdminCommunity();

  const tabStackRoute = state.routes.find((r: any) => r.name === 'AdminTabStack');
  const innerState = tabStackRoute?.state;
  // El estado del navegador interno se crea de forma perezosa: en la primera entrada
  // aún es undefined, así que usamos la ruta inicial (Resumen) como respaldo.
  const focusedTabRoute: string =
    innerState?.routes?.[innerState.index]?.name ?? 'Admin.Home';

  console.log('%c AdminSidebar focused tab', 'color: cyan', focusedTabRoute);

  const visibleItems = visibleSidebarItems(community);

  const navigateTo = (route: string) => {
    console.log('%c AdminSidebar navigateTo', 'color: cyan', route, 'routes.length:', state.routes.length);

    // Si hay details apilados en el top-level stack, popearlos primero
    // para que el navigate a AdminTabStack haga popTo (en vez de pushear otro)
    const detailCount = state.routes.length - 1;
    if (detailCount > 0) {
      console.log('%c AdminSidebar popping', 'color: cyan', detailCount, 'detail(s)');
      navigation.dispatch(StackActions.pop(detailCount));
    }

    navigation.navigate('AdminTabStack', { screen: route });
  };

  return (
    <View style={tw`w-60 h-100% bg-secondary border-r border-gray-200`}>
      <View style={tw`h-16 w-100% justify-center items-center`}>
        <Illustration name="logo" width={68} height={42} />
      </View>

      <View style={tw`flex-1 py-3`}>
        {visibleItems.map((item, index) => (
          <SidebarRow
            key={`${item.type}-${item.label}-${index}`}
            item={item}
            focusedRoute={focusedTabRoute}
            onNavigate={navigateTo}
          />
        ))}
      </View>
    </View>
  );
}


function SidebarRow({
  item,
  focusedRoute,
  onNavigate,
}: {
  item: SidebarItem;
  focusedRoute: string | undefined;
  onNavigate: (route: string) => void;
}) {
  const isGroupContainingFocused =
    item.type === 'group' &&
    (item.items.some(sub => sub.route === focusedRoute) ||
      (item.behavior === 'landing' && item.landingRoute === focusedRoute));

  const [expanded, setExpanded] = useState(isGroupContainingFocused);

  if (item.type === 'link') {
    const isFocused = focusedRoute === item.route;
    return (
      <Pressable
        style={tw.style(
        `row items-center px-4 py-2.5 mx-2 my-0.5 rounded-md`,
          isFocused && `bg-secondary/10`
        )}
        onPress={() => onNavigate(item.route)}
      >
        <Icon
          name={item.icon}
          variant={isFocused ? 'white' : 'default'}
          size={20}
          style={tw`mr-3`}
        />
        <Text
          style={tw.style(
            `text-white text-sm`,
            isFocused && `text-primary font-600`
          )}
        >
          {item.label}
        </Text>
      </Pressable>
    );
  }

  const isLandingFocused =
    item.behavior === 'landing' && item.landingRoute === focusedRoute;

  return (
    <View>
      <Pressable
        style={tw.style(
          `row items-center px-4 py-2.5 mx-2 my-0.5 rounded-md`,
          isLandingFocused && `bg-secondary/10`
        )}
        onPress={() => {
          if (item.behavior === 'landing' && item.landingRoute) {
            onNavigate(item.landingRoute);
            setExpanded(true);
          } else {
            setExpanded(e => !e);
          }
        }}
      >
        <Icon
          name={item.icon}
          variant={isLandingFocused ? 'white' : 'default'}
          size={20}
          style={tw`mr-3`}
        />
        <Text
          style={tw.style(
            `text-white text-sm flex-1`,
            (isLandingFocused) && `text-primary font-600`
          )}
        >
          {item.label}
        </Text>
        <Text style={tw`text-gray-200`}>{expanded ? '▾' : '▸'}</Text>
      </Pressable>

      {expanded &&
        item.items.map(sub => {
          const isFocused = focusedRoute === sub.route;
          return (
            <Pressable
              key={sub.route}
              style={tw.style(
                `row items-center pl-12 pr-4 py-2 mx-2 my-0.5 rounded-md`,
                isFocused && `bg-secondary/10`
              )}
              onPress={() => onNavigate(sub.route)}
            >
              <Text
                style={tw.style(
                  `text-white/90 text-sm`,
                  isFocused && `text-primary font-600`
                )}
              >
                {sub.label}
              </Text>
            </Pressable>
          );
        })}
    </View>
  );
}
